'use client';

import { useEffect, useState } from "react"
import { MapPin, Calendar, Save, ArrowLeft, Plus, Trash2 } from "lucide-react"
import CompactHeader from "../CompactHeader"
import { useNavigate, Link } from '@tanstack/react-router'
import { 
  Button, 
  Input, 
  Textarea,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  useRepository, 
} from '@city-sage/aurum-avis-labs-components'
import { useForm } from "react-hook-form"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from 'react-i18next'
import { QueryKeys } from "../../lib/QueryKeys"
import { useParams } from "@tanstack/react-router"
import { toast } from 'sonner'
import { ChatButton } from "../chat/ChatButton"
import { StopPreviewCard } from "../StopPreviewCard"
import MiniDayMapPreview from "../MiniDayMapPreview";
import { useDayStops } from "../../hooks/useDayStops";
import { MarkupEditorSheet } from "../MarkupEditorSheet";
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

interface EditDayForm {
  title: string
  description: string
}

export default function ItineraryDayEditPage() {
  const { id: itineraryId, dayId } = useParams({ from: '/itineraries/$id/days/$dayId/edit' })
  const navigate = useNavigate()
  const { api } = useRepository()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isMarkupEditorOpen, setIsMarkupEditorOpen] = useState(false)

  // Drag and drop sensors - configured for mobile touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before activating drag
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250, // 250ms delay before activating drag on touch
        tolerance: 5, // Allow 5px of movement during delay
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Fetch itinerary details
  const { data: itinerary, isLoading: isLoadingItinerary } = useQuery({
    queryKey: QueryKeys.itinerary.details(itineraryId),
    queryFn: () => api.findOneItinerary({ id: itineraryId }),
    enabled: !!itineraryId
  })

  // Fetch day details
  const { data: day, isLoading: isLoadingDay } = useQuery({
    queryKey: QueryKeys.itinerary.day(itineraryId!, dayId!),
    queryFn: () => api.findOneItineraryDay({ itineraryId: itineraryId!, id: dayId }),
    enabled: !!dayId
  })

  // Fetch day stops using the custom hook
  const { data: dayStops, isLoading: isLoadingStops } = useDayStops({
    itineraryId: itineraryId!,
    dayId: dayId!
  })


  // Form setup
  const form = useForm<EditDayForm>({
    defaultValues: {
      title: "",
      description: "",
    },
  })

  const { register, formState: { isDirty } } = form

  // Reset form when day loads
  useEffect(() => {
    if (day) {
      form.reset({
        title: day.title || `Day ${day.number}`,
        description: day.description || "",
      })
    }
  }, [day, form])

  // Update mutation - simplified for now
  const updateMutation = useMutation({
    mutationFn: (data: EditDayForm) => {
      return api.updateDay({
        itineraryId: itineraryId!,
        id: dayId!,
        updateDayRequest: {
          title: data.title,
          description: data.description,
        }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.day(itineraryId!, dayId!) })
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.days(itineraryId!) })
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.stops(itineraryId!) })
      toast.success("Day saved successfully!")
    },
    onError: () => {
      toast.error("Failed to save day. Please try again.")
    },
  })

  // Delete mutation - API method needs to be implemented
  const deleteMutation = useMutation({
    mutationFn: () => {
        return api.removeDay({
            itineraryId: itineraryId!,
            id: dayId!,
        })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.days(itineraryId!) })
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.stops(itineraryId!) })
      toast.success("Day deleted successfully!")
      navigate({ to: `/itineraries/${itineraryId}/edit` })
    },
    onError: () => {
      toast.error("Failed to delete day. Please try again.")
    },
  })

  // Reorder stops mutation - only updates the dragged stop's index
  const reorderStopsMutation = useMutation({
    mutationFn: async ({ stopId, newIndex }: { stopId: string; newIndex: number }) => {
      return api.updateStop({
        itineraryId: itineraryId!,
        id: stopId,
        updateStopRequest: {
          index: newIndex
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.days(itineraryId!) })
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.stops(itineraryId!, dayId!) })
      toast.success("Stops reordered successfully!")
    },
    onError: () => {
      toast.error("Failed to reorder stops. Please try again.")
    },
  })

  const handleSubmit = form.handleSubmit(async (data) => {
    updateMutation.mutate(data)
  })

  const handleAddStopClick = (e: React.MouseEvent) => {
    if (isDirty) {
      e.preventDefault()
      alert("Please save your changes before adding a new stop.")
      return
    }
  }

  // Drag start handler for haptic feedback
  const handleDragStart = () => {
    // Provide haptic feedback on mobile devices
    if ('vibrate' in navigator) {
      navigator.vibrate(50); // Short vibration
    }
  }

  // Drag end handler for reordering stops
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || !dayStops) return

    if (active.id !== over.id) {
      const oldIndex = dayStops.findIndex(stop => stop.id === active.id)
      const newIndex = dayStops.findIndex(stop => stop.id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedStops = arrayMove(dayStops, oldIndex, newIndex)
        
        // The new position in 1-based indexing
        const newPosition = newIndex + 1

        // Optimistic update
        queryClient.setQueryData(
          QueryKeys.itinerary.stops(itineraryId!, dayId!),
          reorderedStops
        )

        // Send the reorder request - only update the dragged stop
        // The backend will handle reordering other stops automatically
        reorderStopsMutation.mutate({ 
          stopId: active.id as string, 
          newIndex: newPosition 
        })
      }
    }
  }



  if (isLoadingItinerary || isLoadingDay || isLoadingStops) {
    return <div className="flex items-center justify-center min-h-[50dvh]">Loading...</div>
  }

  if (!itinerary || !day) {
    return <div className="flex items-center justify-center min-h-[50dvh]">Day not found</div>
  }

  return (
    <div className="min-h-dvh bg-gray-50 pb-24">
      <CompactHeader 
        backTo={`/itineraries/${itineraryId}/edit`}
        icon={Calendar}
        title={`Edit Day ${day.number}`}
        subtitle={itinerary.title}
      />

      <div className="space-y-2">
        {/* Day Map Section */}
        <div className="bg-white px-6 py-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Day {day.number} Route</h2>
          </div>

          <MiniDayMapPreview 
            stops={dayStops || []} 
            dayNumber={day.number} 
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-2">
          {/* Day Details Section */}
          <div className="bg-white px-6 py-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Day Details</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Day Title</label>
                <Input
                  placeholder="Enter day title..."
                  {...register("title")}
                  className="text-lg font-semibold"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Day Description</label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMarkupEditorOpen(true)}
                    className="text-xs underline"
                  >
                    Markup Editor
                  </Button>
                </div>
                <Textarea
                  placeholder="Describe what this day is about..."
                  {...register("description")}
                  rows={4}
                  className="w-full"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate({ to: `/itineraries/${itineraryId}/edit` })}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={updateMutation.isPending}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {updateMutation.isPending ? "Saving..." : "Save Day Changes"}
                </Button>
              </div>
            </div>
          </div>

          {/* Stops Section */}
          <div className="bg-white px-6 py-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">Stops for Day {day.number}</h2>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">{dayStops?.length || 0} stops</span>
                <Link 
                  to={`/itineraries/${itineraryId}/days/${dayId}/stops/form`}
                  onClick={handleAddStopClick}
                >
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    Add Stop
                  </Button>
                </Link>
              </div>
            </div>

            {/* Render stops or empty state */}
            {dayStops && dayStops.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext 
                  items={dayStops.map(stop => stop.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3" style={{ touchAction: 'pan-y' }}>
                    {dayStops.map((stop, index) => (
                      <StopPreviewCard 
                        itineraryId={itineraryId}
                        dayId={dayId}
                        key={stop.id} 
                        stop={stop} 
                        stopNumber={index + 1}
                        isDraggable={true}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-8">
                <div className="text-center">
                  <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No stops added yet</h3>
                  <p className="text-gray-500">
                    Start building your day by adding stops to visit
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="bg-white px-6 py-6">
            <div className="space-y-3">
          

              {/* Delete Button with Confirmation Dialog */}
              <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    className="w-full"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Day
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Day {day?.number}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete Day {day?.number} 
                      "{day?.title || `Day ${day?.number}`}" from your itinerary.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        deleteMutation.mutate()
                        setIsDeleteDialogOpen(false)
                      }}
                      className="bg-red-600 hover:bg-red-700"
                      disabled={deleteMutation.isPending}
                    >
                      {deleteMutation.isPending ? "Deleting..." : "Delete Day"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </form>
      </div>

      <ChatButton />

      {/* Markup Editor Sheet */}
      <MarkupEditorSheet
        isOpen={isMarkupEditorOpen}
        onOpenChange={setIsMarkupEditorOpen}
        value={form.watch('description') || ''}
        onSave={(value: string) => {
          form.setValue('description', value, { shouldDirty: true })
          setIsMarkupEditorOpen(false)
        }}
      />
    </div>
  )
} 